<template>
  <div :class="classes" tabindex="0" @click="$refs.input.click()">
    <input
      v-bind="$attrs"
      type="file"
      :multiple="multiple"
      :class="inputFieldClasses"
      ref="input"
      @change="onInputFileChange"
    />
    <m4-button type="button" class="file-input__button" variant="info" v-if="!computedValue && !$attrs.placeholder">
      {{ buttonText ?? $t("fileInput.Browse") }}
    </m4-button>
    <div class="file-input__text">{{ text }}</div>
    <label v-if="$attrs.placeholder" :for="$attrs.id" :class="floatingPlaceholderClasses">
      <m4-icon icon="paper-clip"></m4-icon> {{ $attrs.placeholder }}
    </label>
  </div>
</template>

<script>
import { wrapInArray, deepEqual, truncate } from "../../../utils/helpers";

export default {
  name: "M4FileInput",
  model: {
    prop: "modelValue",
    event: "change"
  },
  data() {
    return {
      newValue: null,
      focus: false
    };
  },
  props: {
    multiple: {
      type: Boolean,
      default: false
    },
    error: {
      type: Boolean,
      default: false
    },
    buttonText: {
      type: String
    },
    modelValue: {
      default: undefined,
      validator: (val) => {
        return wrapInArray(val).every((v) => v != null && typeof v === "object");
      }
    },
    value: {
      default: undefined,
      validator: (val) => {
        return wrapInArray(val).every((v) => v != null && typeof v === "object");
      }
    }
  },
  emits: ["update:modelValue", "change"],
  computed: {
    classes() {
      let classes = ["file-input"];
      if (this.$attrs.placeholder) {
        classes.push("file-input--floating-placeholder");
      }

      if (this.newValue) {
        classes.push("file-input--has-value");
      }

      return classes;
    },
    inputFieldClasses() {
      let classes = ["file-input__field"];
      return classes;
    },
    floatingPlaceholderClasses() {
      let classes = ["file-input__floating-placeholder"];
      if (this.error) {
        classes.push("file-input__floating-placeholder--error");
      }
      return classes;
    },
    isTextarea() {
      return this.$attrs.type === "textarea";
    },
    computedValue: {
      get() {
        return this.newValue;
      },
      set(value) {
        this.newValue = value;
        this.$emit("update:modelValue", value);
      }
    },
    isMultiple() {
      return this.multiple;
    },
    text() {
      const files = wrapInArray(this.computedValue);
      const display = files.map((file) => {
        const { name } = file;
        return truncate(name, 20, "middle");
      });
      return display.join(", ");
    }
  },
  methods: {
    onInputFileChange(e) {
      const files = e.target.files;
      this.computedValue = this.isMultiple ? files : files[0];
      this.$emit("change", e);
    },
    resetInput() {
      this.newValue = null;
    }
  },
  watch: {
    modelValue(v) {
      // The value of a file input can not be changed programatically, it can only be cleared.
      const value = this.isMultiple ? v : v ? [v] : [];
      if (!deepEqual(value, this.$refs.input.files) || v === null) {
        // When the value is changed programatically, clear the input field value.
        // This is necessary to ensure that the onChange event is fired again when the user selects the same file again.
        this.$refs.input.value = "";
        if (v === null) {
          this.newValue = null;
        }
      }
    },
    value(v) {
      // The value of a file input can not be changed programatically, it can only be cleared.
      const value = this.isMultiple ? v : v ? [v] : [];
      if (!deepEqual(value, this.$refs.input.files) || v === null) {
        // When the value is changed programatically, clear the input field value.
        // This is necessary to ensure that the onChange event is fired again when the user selects the same file again.
        this.$refs.input.value = "";
        if (v === null) {
          this.newValue = null;
        }
      }
    }
  }
};
</script>
